import * as D from "./dimensions";
import { PALETTE, Rgb, mix } from "./palette";
import { PocketTone, SLOT_COUNT, TAU, WheelLayout, pocketTone } from "./wheelLayout";

// The wheel, built as plain triangle soup on the CPU once at start-up. Three
// meshes, because three things move independently: the bowl the ball runs in
// stands still, the head with the pockets turns, and the ball goes its own way.

/** position(3) + normal(3) + colour(3) + (field, material)(2). */
export const FLOATS_PER_VERTEX = 11;

/** Which lighting model a surface gets in the shader. */
export const MATERIAL = {
  /** The felt the wheel stands on: no highlight, darkened towards the wheel. */
  felt: 0,
  /** Lacquer — the painted pocket floors and the number band. Deep gloss. */
  lacquer: 1,
  /** Brass — the ring, the cone's hub, the spindle, the deflectors. */
  metal: 2,
  /** The ball: bright, hard highlight. */
  ball: 3,
  /** Polished rosewood: lacquer's gloss, with grain under it. */
  wood: 4,
  /** Steel — the frets. Cooler and harder than the brass. */
  steel: 5,
} as const;

export interface MeshData {
  vertices: Float32Array;
  indices: Uint32Array;
}

export type V3 = [number, number, number];

const sub = (a: V3, b: V3): V3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a: V3, b: V3): V3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const norm = (v: V3): V3 => {
  const l = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / l, v[1] / l, v[2] / l];
};
/** A point on a circle of radius `r` at height `y`. */
const at = (r: number, angle: number, y: number): V3 => [
  r * Math.cos(angle),
  y,
  r * Math.sin(angle),
];

class MeshBuilder {
  private readonly data: number[] = [];
  private readonly index: number[] = [];

  vertex(p: V3, n: V3, colour: Rgb, slot: number, material: number): number {
    this.data.push(p[0], p[1], p[2], n[0], n[1], n[2], colour[0], colour[1], colour[2], slot, material);
    return this.data.length / FLOATS_PER_VERTEX - 1;
  }

  triangle(a: number, b: number, c: number) {
    this.index.push(a, b, c);
  }

  /** A flat triangle, for the faces of the deflectors. */
  face(p0: V3, p1: V3, p2: V3, colour: Rgb, slot: number, material: number) {
    const n = norm(cross(sub(p1, p0), sub(p2, p0)));
    this.triangle(
      this.vertex(p0, n, colour, slot, material),
      this.vertex(p1, n, colour, slot, material),
      this.vertex(p2, n, colour, slot, material),
    );
  }

  /** A flat quad, wound p0→p1→p2→p3 around its edge. */
  quad(p0: V3, p1: V3, p2: V3, p3: V3, colour: Rgb, slot: number, material: number) {
    const n = norm(cross(sub(p1, p0), sub(p3, p0)));
    const a = this.vertex(p0, n, colour, slot, material);
    const b = this.vertex(p1, n, colour, slot, material);
    const c = this.vertex(p2, n, colour, slot, material);
    const d = this.vertex(p3, n, colour, slot, material);
    this.triangle(a, b, c);
    this.triangle(a, c, d);
  }

  /**
   * A closed box given its bottom and top faces, wound the same way round.
   * `topColour` is separate because at this shallow a camera angle, a fret's
   * top edge is most of what you see of it.
   */
  prism(
    bottom: V3[],
    top: V3[],
    colour: Rgb,
    slot: number,
    material: number,
    topColour = colour,
  ) {
    for (let i = 0; i < 4; i++) {
      const j = (i + 1) % 4;
      this.quad(bottom[i], bottom[j], top[j], top[i], colour, slot, material);
    }
    this.quad(top[0], top[1], top[2], top[3], topColour, slot, material);
    this.quad(bottom[3], bottom[2], bottom[1], bottom[0], colour, slot, material);
  }

  build(): MeshData {
    return { vertices: new Float32Array(this.data), indices: new Uint32Array(this.index) };
  }
}

/** One station of a lathe profile: where the surface is, and which way it faces. */
interface ProfilePoint {
  r: number;
  y: number;
  /** Surface normal in the (radius, height) plane. */
  nr: number;
  ny: number;
}

/**
 * Turns a list of (radius, height) points into a profile with smooth normals,
 * taken perpendicular to the line through each point's neighbours. `outward`
 * flips which side of the surface faces the viewer.
 */
function profile(points: Array<[number, number]>, outward = true): ProfilePoint[] {
  return points.map(([r, y], i) => {
    const prev = points[Math.max(0, i - 1)];
    const next = points[Math.min(points.length - 1, i + 1)];
    const dr = next[0] - prev[0];
    const dy = next[1] - prev[1];
    const len = Math.hypot(dr, dy) || 1;
    const sign = outward ? 1 : -1;
    return { r, y, nr: (sign * dy) / len, ny: (-sign * dr) / len };
  });
}

/** Sweeps a profile all the way round the Y axis. */
function revolve(
  builder: MeshBuilder,
  points: ProfilePoint[],
  segments: number,
  colour: Rgb | ((t: number) => Rgb),
  material: number,
  slot = -1,
) {
  const colourAt = typeof colour === "function" ? colour : () => colour;
  const rows: number[][] = [];
  for (let i = 0; i < points.length; i++) {
    const p = points[i];
    const row: number[] = [];
    const c = colourAt(points.length === 1 ? 0 : i / (points.length - 1));
    for (let s = 0; s <= segments; s++) {
      const a = (s / segments) * TAU;
      const cos = Math.cos(a);
      const sin = Math.sin(a);
      row.push(
        builder.vertex(
          [p.r * cos, p.y, p.r * sin],
          norm([p.nr * cos, p.ny, p.nr * sin]),
          c,
          slot,
          material,
        ),
      );
    }
    rows.push(row);
  }
  for (let i = 0; i + 1 < rows.length; i++) {
    for (let s = 0; s < segments; s++) {
      builder.triangle(rows[i][s], rows[i][s + 1], rows[i + 1][s + 1]);
      builder.triangle(rows[i][s], rows[i + 1][s + 1], rows[i + 1][s]);
    }
  }
}

/** A flat disc facing up, as a fan from its centre. */
function disc(builder: MeshBuilder, radius: number, y: number, segments: number, colour: Rgb, material: number) {
  const up: V3 = [0, 1, 0];
  const centre = builder.vertex([0, y, 0], up, colour, -1, material);
  const ring: number[] = [];
  for (let s = 0; s <= segments; s++) {
    const a = (s / segments) * TAU;
    ring.push(builder.vertex(at(radius, a, y), up, colour, -1, material));
  }
  for (let s = 0; s < segments; s++) builder.triangle(centre, ring[s], ring[s + 1]);
}

/**
 * A tapered tube between two points, capped at both ends — the arms of the
 * spindle handle, which are the one part of the wheel that doesn't lie on a
 * circle about the Y axis and so can't come off the lathe above.
 */
function tube(
  builder: MeshBuilder,
  from: V3,
  to: V3,
  radiusFrom: number,
  radiusTo: number,
  sides: number,
  colour: Rgb,
  material: number,
) {
  const axis = norm(sub(to, from));
  // Any direction that isn't the axis, to get a frame to sweep the circle in.
  const helper: V3 = Math.abs(axis[1]) > 0.9 ? [1, 0, 0] : [0, 1, 0];
  const u = norm(cross(helper, axis));
  const v = cross(axis, u);

  const rings: number[][] = [];
  for (const [origin, radius] of [
    [from, radiusFrom],
    [to, radiusTo],
  ] as Array<[V3, number]>) {
    const ring: number[] = [];
    for (let i = 0; i <= sides; i++) {
      const a = (i / sides) * TAU;
      const n = norm([
        u[0] * Math.cos(a) + v[0] * Math.sin(a),
        u[1] * Math.cos(a) + v[1] * Math.sin(a),
        u[2] * Math.cos(a) + v[2] * Math.sin(a),
      ]);
      const p: V3 = [
        origin[0] + n[0] * radius,
        origin[1] + n[1] * radius,
        origin[2] + n[2] * radius,
      ];
      ring.push(builder.vertex(p, n, colour, -1, material));
    }
    rings.push(ring);
  }
  for (let i = 0; i < sides; i++) {
    builder.triangle(rings[0][i], rings[0][i + 1], rings[1][i + 1]);
    builder.triangle(rings[0][i], rings[1][i + 1], rings[1][i]);
  }
  // Caps, so an arm seen end-on isn't a hole.
  for (const [ring, origin, dir] of [
    [rings[0], from, [-axis[0], -axis[1], -axis[2]] as V3],
    [rings[1], to, axis],
  ] as Array<[number[], V3, V3]>) {
    const centre = builder.vertex(origin, dir, colour, -1, material);
    for (let i = 0; i < sides; i++) builder.triangle(centre, ring[i], ring[i + 1]);
  }
}

/** A sphere: the ball, and the knobs on the ends of the spindle handle. */
function sphere(
  builder: MeshBuilder,
  centre: V3,
  radius: number,
  rings: number,
  segments: number,
  colour: Rgb,
  material: number,
) {
  const rows: number[][] = [];
  for (let i = 0; i <= rings; i++) {
    const phi = (i / rings) * Math.PI;
    const row: number[] = [];
    for (let s = 0; s <= segments; s++) {
      const theta = (s / segments) * TAU;
      const n: V3 = [
        Math.sin(phi) * Math.cos(theta),
        Math.cos(phi),
        Math.sin(phi) * Math.sin(theta),
      ];
      row.push(
        builder.vertex(
          [centre[0] + n[0] * radius, centre[1] + n[1] * radius, centre[2] + n[2] * radius],
          n,
          colour,
          -1,
          material,
        ),
      );
    }
    rows.push(row);
  }
  for (let i = 0; i < rings; i++) {
    for (let s = 0; s < segments; s++) {
      builder.triangle(rows[i][s], rows[i][s + 1], rows[i + 1][s + 1]);
      builder.triangle(rows[i][s], rows[i + 1][s + 1], rows[i + 1][s]);
    }
  }
}

/**
 * Rosewood, light on the turned crown of the rim and deep down the sides:
 * `t` runs 0 (the polish catching the light) to 1 (in shade).
 */
function woodTone(t: number): Rgb {
  return t < 0.5
    ? mix(PALETTE.rosewoodLit, PALETTE.rosewood, t * 2)
    : mix(PALETTE.rosewood, PALETTE.rosewoodDeep, (t - 0.5) * 2);
}

// ---------------------------------------------------------------------------
// The static half: the felt, the wheel's body, the bowl the ball runs round,
// and the deflectors it rattles off.
// ---------------------------------------------------------------------------

export function buildStaticMesh(): MeshData {
  const b = new MeshBuilder();

  // The table. Big enough to run past the edge of any sensible framing, so the
  // wheel never looks like it is standing on a saucer.
  disc(b, 4.2, D.BASE_Y - 0.001, 72, PALETTE.felt, MATERIAL.felt);

  // The brass ring the number band runs against. It is a band of the bowl's
  // own surface rather than anything standing on it — the ball is simulated
  // against that curve, so a ring with a height of its own would be a ledge
  // the ball passes straight through.
  const ringOuter = D.R_BAND_OUT + D.RING_WIDTH;
  const ringSteps = 3;
  const ringBand: Array<[number, number]> = [];
  for (let i = 0; i <= ringSteps; i++) {
    const r = D.R_BAND_OUT + (D.RING_WIDTH * i) / ringSteps;
    ringBand.push([r, D.bowlSurfaceY(r)]);
  }
  revolve(b, profile(ringBand), 96, (t) => mix(PALETTE.brassLit, PALETTE.brass, t), MATERIAL.metal);

  // The bowl: one surface from the brass ring out to the rim, sampled off the
  // very curve the ball is simulated against. Denser sampling where it turns
  // up into the wall of the ball track. Rosewood, deepest at the bottom of the
  // dish where the light doesn't reach and lifting towards the ball track.
  const wallSteps = 22;
  const wall: Array<[number, number]> = [];
  for (let i = 0; i <= wallSteps; i++) {
    const t = i / wallSteps;
    const r = ringOuter + (D.R_RIM - ringOuter) * Math.pow(t, 0.85);
    wall.push([r, D.bowlSurfaceY(r)]);
  }
  const rimTop = D.bowlSurfaceY(D.R_RIM);

  revolve(b, profile(wall), 96, (t) => woodTone(0.92 - 0.5 * t), MATERIAL.wood);

  // The rim: the wheel's widest point, turned over into a rounded crown and
  // polished. Wood all the way over rather than a brass band — on the wheel
  // this is drawn from, the only metal above the pockets is the ring at the
  // bottom of the bowl and the diamonds set into it.
  revolve(
    b,
    profile([
      [D.R_RIM, rimTop],
      [D.R_RIM + 0.035, rimTop + 0.016],
      [D.R_RIM + 0.065, rimTop + 0.012],
      [D.R_BODY, rimTop - 0.03],
    ]),
    96,
    (t) => woodTone(0.42 - 0.34 * Math.cos(t * Math.PI)),
    MATERIAL.wood,
  );
  // …and the body falling away beneath it, into shade.
  revolve(
    b,
    profile([
      [D.R_BODY, rimTop - 0.03],
      [D.R_RIM + 0.075, rimTop - 0.2],
      [D.R_RIM - 0.02, D.BASE_Y + 0.05],
      [D.R_RIM - 0.12, D.BASE_Y],
    ]),
    96,
    (t) => woodTone(0.55 + 0.45 * t),
    MATERIAL.wood,
  );

  // Deflectors: the brass diamonds set into the wall, alternating between two
  // radii the way a real bowl's do. Positions come from dimensions.ts, so the
  // ball rattles off the ones you can see.
  //
  // A diamond, not a pyramid on a square: the plan is a lozenge with its long
  // axis pointing at the middle of the wheel, and it stands low — a little
  // over a third of its own length. What a real one is is a piece of metal let
  // into the wood, which is why it also gets the flat plate under it: the seam
  // round the edge is what makes it read as inlaid rather than as glued on.
  const facet = mix(PALETTE.brass, PALETTE.brassLit, 0.4);
  for (const deflector of D.DEFLECTORS) {
    const { angle: a, radius: r, turned } = deflector;
    // A turned diamond is the same lozenge lying across the wheel instead of
    // pointing at the middle of it — the long axis and the short one traded.
    const long = D.DEFLECTOR_SIZE * (turned ? 0.62 : 1);
    const wide = D.DEFLECTOR_SIZE * (turned ? 1 : 0.62);
    /** A corner of the lozenge, lying on the dish rather than on a flat disc. */
    const corner = (dr: number, dw: number, lift: number): V3 =>
      at(r + dr, a + dw / r, D.bowlSurfaceY(r + dr) + lift);

    const seam = 1.3;
    b.quad(
      corner(-long * seam, 0, 0.001),
      corner(0, -wide * seam, 0.001),
      corner(long * seam, 0, 0.001),
      corner(0, wide * seam, 0.001),
      PALETTE.brassDeep,
      -1,
      MATERIAL.metal,
    );

    const base: V3[] = [
      corner(-long, 0, 0.002),
      corner(0, -wide, 0.002),
      corner(long, 0, 0.002),
      corner(0, wide, 0.002),
    ];
    const apex: V3 = at(r, a, D.bowlSurfaceY(r) + D.DEFLECTOR_SIZE * 0.38);
    for (let k = 0; k < 4; k++) {
      const j = (k + 1) % 4;
      b.face(base[k], base[j], apex, facet, -1, MATERIAL.metal);
    }
  }

  return b.build();
}

// ---------------------------------------------------------------------------
// The turning half: 22 pockets, the frets between them, and the cone.
// ---------------------------------------------------------------------------

/**
 * What each kind of field is painted. Two tones apiece: the lacquer as the
 * light finds it, and the same colour in shade, for the deep end of a pocket
 * and for the wall the number band stands on. Exported because the 2D painter
 * has to reach for exactly the same two.
 */
export const POCKET_PAINT: Record<PocketTone, { face: Rgb; deep: Rgb }> = {
  ink: { face: PALETTE.pocketBlack, deep: PALETTE.pocketBlackDeep },
  clay: { face: PALETTE.pocketRed, deep: PALETTE.pocketRedDeep },
  gold: { face: PALETTE.gold, deep: PALETTE.goldDeep },
};

export function buildHeadMesh(layout: WheelLayout): MeshData {
  const b = new MeshBuilder();
  const floorY = D.POCKET_FLOOR_Y;
  const step = TAU / SLOT_COUNT;
  const arcSteps = 5;
  const bandY = (r: number) => D.bowlSurfaceY(r);
  const bandSteps = 3;
  const rMid = D.R_POCKET_MID;

  for (let slot = 0; slot < SLOT_COUNT; slot++) {
    const paint = POCKET_PAINT[pocketTone(slot, layout)];
    // Field 0's centre sits half a step round from the seam, matching
    // slotAngle() — the whole point being that the ball's landing angle and
    // the pocket drawn here are the same number.
    const from = slot * step;

    for (let i = 0; i < arcSteps; i++) {
      const a0 = from + (step * i) / arcSteps;
      const a1 = from + (step * (i + 1)) / arcSteps;
      // The floor, in two bands: a pocket is deepest at the inner end, where
      // the cone leans over it, and that shading is most of what makes it
      // read as a compartment rather than as a painted stripe.
      b.quad(
        at(D.R_POCKET_IN, a0, floorY),
        at(rMid, a0, floorY),
        at(rMid, a1, floorY),
        at(D.R_POCKET_IN, a1, floorY),
        mix(paint.face, paint.deep, 0.42),
        slot,
        MATERIAL.lacquer,
      );
      b.quad(
        at(rMid, a0, floorY),
        at(D.R_POCKET_OUT, a0, floorY),
        at(D.R_POCKET_OUT, a1, floorY),
        at(rMid, a1, floorY),
        mix(paint.face, paint.deep, 0.08),
        slot,
        MATERIAL.lacquer,
      );

      // The wall at the outer edge of the pocket. This is what a settled ball
      // rests against, and it turns with the wheel: a ball held by a standing
      // surface would never settle while the wheel was still turning.
      b.quad(
        at(D.R_POCKET_OUT, a0, floorY),
        at(D.R_POCKET_OUT, a0, D.POCKET_LIP_Y),
        at(D.R_POCKET_OUT, a1, D.POCKET_LIP_Y),
        at(D.R_POCKET_OUT, a1, floorY),
        paint.deep,
        slot,
        MATERIAL.lacquer,
      );

      // The number band: the sloping ring outside the pockets, in the field's
      // own colour, sitting on the bowl's curve so head and bowl are one
      // surface. On a real wheel this is where the number is painted; here it
      // is the widest, flattest piece of a field's colour on the whole wheel,
      // which is what lets you tell the fields apart at a glance.
      for (let k = 0; k < bandSteps; k++) {
        const r0 = D.R_POCKET_OUT + ((D.R_BAND_OUT - D.R_POCKET_OUT) * k) / bandSteps;
        const r1 = D.R_POCKET_OUT + ((D.R_BAND_OUT - D.R_POCKET_OUT) * (k + 1)) / bandSteps;
        b.quad(
          at(r0, a0, bandY(r0)),
          at(r1, a0, bandY(r1)),
          at(r1, a1, bandY(r1)),
          at(r0, a1, bandY(r0)),
          mix(paint.face, PALETTE.bone, 0.045),
          slot,
          MATERIAL.lacquer,
        );
      }

      // The skirt under the band, so the head reads as a solid disc from the
      // side rather than as a paper ring.
      b.quad(
        at(D.R_BAND_OUT, a0, floorY - 0.06),
        at(D.R_BAND_OUT, a0, bandY(D.R_BAND_OUT)),
        at(D.R_BAND_OUT, a1, bandY(D.R_BAND_OUT)),
        at(D.R_BAND_OUT, a1, floorY - 0.06),
        PALETTE.brassDeep,
        slot,
        MATERIAL.metal,
      );
    }
  }

  // Frets: the steel dividers standing between the pockets. Two pieces each,
  // because a fret is let into the cone at one end and crosses the number band
  // at the other: over the pockets its top edge is level, and where it laps
  // onto the cone it tapers away into the slope.
  const fretHalf = D.FRET_HALF_WIDTH;
  // The sides of a fret face into a pocket and are mostly in its shade; only
  // the top edge is turned towards the light.
  const fretSide = mix(PALETTE.silver, PALETTE.silverDeep, 0.62);
  const topY = D.FRET_TOP_Y;
  const coneFoot = D.coneSurfaceY(D.FRET_INNER_R);
  const corners = (rIn: number, rOut: number, a: number, yIn: number, yOut: number): V3[] => [
    at(rIn, a - fretHalf / rIn, yIn),
    at(rOut, a - fretHalf / rOut, yOut),
    at(rOut, a + fretHalf / rOut, yOut),
    at(rIn, a + fretHalf / rIn, yIn),
  ];

  for (let i = 0; i < SLOT_COUNT; i++) {
    const a = i * step;
    // The lap onto the cone: buried in the slope at the inner end, rising to
    // full height where the pockets start.
    b.prism(
      corners(D.FRET_INNER_R, D.R_POCKET_IN, a, coneFoot - 0.02, floorY - 0.02),
      corners(D.FRET_INNER_R, D.R_POCKET_IN, a, coneFoot + 0.008, topY),
      fretSide,
      -1,
      MATERIAL.steel,
      PALETTE.silver,
    );
    // Over the pockets and across the band, with a level top.
    b.prism(
      corners(D.R_POCKET_IN, D.FRET_OUTER_R, a, floorY - 0.02, floorY - 0.02),
      corners(D.R_POCKET_IN, D.FRET_OUTER_R, a, topY, topY),
      fretSide,
      -1,
      MATERIAL.steel,
      PALETTE.silver,
    );
  }

  // The cone: rosewood, like the bowl it sits in, and dark enough that the eye
  // goes to the pockets rather than to the middle of the wheel.
  const coneSteps = 18;
  const cone: Array<[number, number]> = [];
  for (let i = 0; i <= coneSteps; i++) {
    const r = Math.max(D.R_POCKET_IN * (1 - i / coneSteps), 0.055);
    cone.push([r, D.coneSurfaceY(r)]);
  }
  revolve(b, profile(cone), 84, (t) => woodTone(0.95 - 0.2 * t), MATERIAL.wood);

  // The fret lines carried on up the cone, as inlaid strips rather than as
  // more standing steel: a fret that kept its height over the cone would be a
  // wall across the middle of the wheel, and on a real one it isn't — the
  // dividers die away into the slope and are only a line by the time they get
  // there.
  const inlaySteps = 6;
  const inlayHalf = D.FRET_HALF_WIDTH * 0.62;
  for (let i = 0; i < SLOT_COUNT; i++) {
    const a = i * step;
    for (let k = 0; k < inlaySteps; k++) {
      const r0 = 0.075 + ((D.FRET_INNER_R - 0.075) * k) / inlaySteps;
      const r1 = 0.075 + ((D.FRET_INNER_R - 0.075) * (k + 1)) / inlaySteps;
      // Lifted off the cone by a hair, so it doesn't fight the cone for the
      // same depth.
      const y0 = D.coneSurfaceY(r0) + 0.002;
      const y1 = D.coneSurfaceY(r1) + 0.002;
      b.quad(
        at(r0, a - inlayHalf / r0, y0),
        at(r1, a - inlayHalf / r1, y1),
        at(r1, a + inlayHalf / r1, y1),
        at(r0, a + inlayHalf / r0, y0),
        PALETTE.silver,
        -1,
        MATERIAL.steel,
      );
    }
  }

  // The brass turret, and the spindle handle across the top of it — the piece
  // a croupier actually takes hold of to set the wheel going.
  revolve(
    b,
    profile([
      [0.055, D.coneSurfaceY(0.055)],
      [0.062, D.CONE_TOP_Y + 0.02],
      [0.045, D.CONE_TOP_Y + 0.06],
      [0.032, D.HANDLE_Y - 0.01],
      [0.03, D.TURRET_TOP_Y - 0.02],
      [0.0, D.TURRET_TOP_Y],
    ]),
    36,
    (t) => mix(PALETTE.brass, PALETTE.brassLit, t),
    MATERIAL.metal,
  );

  const handleY = D.HANDLE_Y;
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * TAU + Math.PI / 4;
    const cos = Math.cos(a);
    const sin = Math.sin(a);
    const root: V3 = [cos * 0.02, handleY + 0.004, sin * 0.02];
    const tip: V3 = [cos * D.HANDLE_ARM, handleY - 0.022, sin * D.HANDLE_ARM];
    tube(b, root, tip, D.HANDLE_THICKNESS, D.HANDLE_THICKNESS * 0.6, 10, PALETTE.brass, MATERIAL.metal);
    sphere(b, tip, D.HANDLE_THICKNESS * 1.35, 8, 12, PALETTE.brassLit, MATERIAL.metal);
  }
  // The boss where the four arms meet.
  sphere(b, [0, handleY + 0.01, 0], 0.042, 10, 16, PALETTE.brass, MATERIAL.metal);

  return b.build();
}

// ---------------------------------------------------------------------------

export function buildBallMesh(): MeshData {
  const b = new MeshBuilder();
  sphere(b, [0, 0, 0], D.BALL_RADIUS, 18, 28, PALETTE.bone, MATERIAL.ball);
  return b.build();
}
